<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" class="text-h5">
        {{ $t('generic.settings') }}
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-radio-group
          v-model="userDateFormat"
          :disabled="isLoading"
          density="compact"
          column
          data-cc="user-settings-date-format"
        >
          <template #label>
            <div class="table-header">
              {{ $t('userProfile.settings.formatLabels.date') }}
            </div>
          </template>
          <v-radio
            :label="`DD.MM.YYYY (${$dayjs().format('DD.MM.YYYY')})`"
            value="DATE_L_PERIOD"
            data-cc="user-settings-date-format-little-endian"
          />
          <v-radio
            :label="`DD/MM/YYYY (${$dayjs().format('DD/MM/YYYY')})`"
            value="DATE_L_SLASH"
            data-cc="user-settings-date-format-little-endian-slash"
          />
          <v-radio
            :label="`MM/DD/YYYY (${$dayjs().format('MM/DD/YYYY')})`"
            value="DATE_B_SLASH"
            data-cc="user-settings-date-format-medium-endian-slash"
          />
          <v-radio
            :label="`YYYY-MM-DD (${$dayjs().format('YYYY-MM-DD')})`"
            value="DATE_M_DASH"
            data-cc="user-settings-date-format-big-endian-dash"
          />
        </v-radio-group>
      </v-col>

      <v-col cols="12" md="6">
        <v-radio-group
          v-model="userTimeFormat"
          :disabled="isLoading"
          density="compact"
          hideDetails
          column
          data-cc="user-settings-time-format"
        >
          <template #label>
            <div class="table-header">
              {{ $t('userProfile.settings.formatLabels.time') }}
            </div>
          </template>
          <v-radio
            :label="`${$t(
              'userProfile.settings.formats.time24'
            )} (${$dayjs().format('HH:mm')})`"
            value="TIME_24_HOURS"
            data-cc="user-settings-time-format-24-hours"
          />
          <v-radio
            :label="`${$t(
              'userProfile.settings.formats.time12'
            )} (${$dayjs().format('hh:mm a')})`"
            value="TIME_12_HOURS_A"
            data-cc="user-settings-time-format-12-hours-a"
          />
        </v-radio-group>
      </v-col>

      <v-col cols="12" md="6">
        <v-radio-group
          v-model="userLengthFormat"
          :disabled="isLoading"
          density="compact"
          column
          data-cc="user-settings-length-format"
        >
          <template #label>
            <div class="table-header">
              {{ $t('userProfile.settings.formatLabels.length') }}
            </div>
          </template>
          <v-radio
            :label="`${$t(
              'userProfile.settings.formats.lengthKilometers'
            )} (1.0 km)`"
            value="LENGTH_METRIC"
            data-cc="user-settings-length-format-kilometers"
          />
          <v-radio
            :label="`${$t(
              'userProfile.settings.formats.lengthMiles'
            )} (0.6214 mi)`"
            value="LENGTH_IMPERIAL"
            data-cc="user-settings-length-format-miles"
          />
        </v-radio-group>
      </v-col>

      <v-col cols="12" md="6">
        <v-radio-group
          v-model="userWeightFormat"
          :disabled="isLoading"
          density="compact"
          column
          data-cc="user-settings-weight-format"
        >
          <template #label>
            <div class="table-header">
              {{ $t('userProfile.settings.formatLabels.weight') }}
            </div>
          </template>
          <v-radio
            :label="`${$t(
              'userProfile.settings.formats.weightKilogram'
            )} (100.0 kg)`"
            value="WEIGHT_METRIC"
            data-cc="user-settings-weight-format-kilogram"
          />
          <v-radio
            :label="`${$t(
              'userProfile.settings.formats.weightPound'
            )} (220.4 lb)`"
            value="WEIGHT_IMPERIAL"
            data-cc="user-settings-weight-format-pound"
          />
        </v-radio-group>
      </v-col>

      <v-col cols="12" md="6">
        <v-radio-group
          v-model="userNumberFormat"
          :disabled="isLoading"
          density="compact"
          hideDetails
          column
          data-cc="user-settings-number-format"
        >
          <template #label>
            <div class="table-header">
              {{ $t('userProfile.settings.formatLabels.decimalPointer') }}
            </div>
          </template>
          <v-radio
            :label="`${$t(
              'userProfile.settings.formats.decimalComma'
            )} (100.100,10)`"
            value="DE"
            data-cc="user-settings-number-format-decimal-comma"
          />
          <v-radio
            :label="`${$t(
              'userProfile.settings.formats.decimalPoint'
            )} (100,100.10)`"
            value="EN"
            data-cc="user-settings-number-format-decimal-point"
          />
        </v-radio-group>
      </v-col>

      <v-col v-if="$userProfile.settings?.hasMfaEnabled" cols="12" md="6">
        <UserMfaStatusComponent />
      </v-col>

      <v-col cols="12">
        <BMButton
          variant="tonal"
          :title="$t('userProfile.cookieSettings')"
          data-cc="user-profile-cookie-settings"
          @clicked="openCookieSettings"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import BMButton from '@/components/common/utilities/BMButton';
import UserMfaStatusComponent from '@/components/common/user/UserMfaStatusComponent.vue';

export default {
  name: 'UserSettingsComponent',
  components: {
    BMButton,
    UserMfaStatusComponent
  },
  data() {
    return {
      isLoading: false,
      userDateFormat: this.$userSettings.userDateFormat,
      userLengthFormat: this.$userSettings.userLengthFormat,
      userNumberFormat: this.$userSettings.userNumberFormat,
      userTimeFormat: this.$userSettings.userTimeFormat,
      userWeightFormat: this.$userSettings.userWeightFormat
    };
  },
  methods: {
    openCookieSettings() {
      window.cookiehub.openSettings();
    },
    async settingsWrapper(key, value) {
      this.isLoading = true;
      if (this.$userProfile.settings?.[key]) {
        await this.$userProfile.patchUserSetting({
          settingsKey: key,
          settingsValue: value
        });
      } else {
        await this.$userProfile.putUserSettings({
          dateFormat: this.userDateFormat,
          lengthFormat: this.userLengthFormat,
          numberFormat: this.userNumberFormat,
          timeFormat: this.userTimeFormat,
          weightFormat: this.userWeightFormat
        });
      }
      this.isLoading = false;
      this.$userSettings = this.$userSettings.updateUserSettings(
        this.$userProfile.settings
      );
    }
  },
  watch: {
    userDateFormat(value) {
      this.settingsWrapper('dateFormat', value);
    },
    userLengthFormat(value) {
      this.settingsWrapper('lengthFormat', value);
    },
    userNumberFormat(value) {
      this.settingsWrapper('numberFormat', value);
    },
    userTimeFormat(value) {
      this.settingsWrapper('timeFormat', value);
    },
    userWeightFormat(value) {
      this.settingsWrapper('weightFormat', value);
    }
  }
};
</script>
